import React, { ReactNode } from 'react';
import ReactTooltip from 'react-tooltip';
import styled from 'styled-components/macro';
import {
  colourGreen03,
  colourGreen07,
  colourGreen09,
  colourNeutral03,
  colourNeutral09,
  colourNeutral10,
  colourRed03,
  colourRed08,
  colourRed09,
  colourYellow02,
  colourYellow05,
  colourYellow09,
} from '../../styling/colours';
import { fontFamily, fontSize03 } from '../../styling/fonts';
import { spacing02 } from '../../styling/spacing';
import { FieldLabel } from './FieldLabel';
import { inputBorderWidth, inputSidePadding } from './inputs/inputStyles';

type Props = {
  name?: string;
  label: string | ReactNode;
  tooltip?: string | null;
  value: string;
  error?: string | null;
  showError?: boolean;
  className?: string;
  colour?: 'green' | 'yellow' | 'red' | null;
  testId?: string;
};

export const ReadOnlyField = ({
  name,
  label,
  tooltip,
  value,
  error = null,
  showError = false,
  className,
  colour,
  testId,
}: Props) => {
  const [backgroundColour, borderColour, textColour] = getTrafficLightColours(colour ?? null);

  return (
    <ReadOnlyFieldContainer className={className}>
      {label && <FieldLabel label={label} fieldName={name || ''} error={error} showError={showError} />}
      <ReadOnlyFieldValueContainer
        data-testid={testId}
        data-tip={tooltip}
        backgroundColour={backgroundColour}
        borderColour={borderColour}
        textColour={textColour}
      >
        <ReadOnlyFieldValue>{value}</ReadOnlyFieldValue>
        {tooltip != null && <ReactTooltip effect="solid" clickable={true} delayShow={500} />}
      </ReadOnlyFieldValueContainer>
    </ReadOnlyFieldContainer>
  );
};

const getTrafficLightColours = (colour: string | null): Array<string> => {
  switch (colour) {
    case 'green':
      return [colourGreen09, colourGreen07, colourGreen03];
    case 'yellow':
      return [colourYellow09, colourYellow05, colourYellow02];
    case 'red':
      return [colourRed09, colourRed08, colourRed03];
    default:
      return [colourNeutral10, colourNeutral09, colourNeutral03];
  }
};

export const ReadOnlyFieldContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const ReadOnlyFieldValueContainer = styled.div<{
  backgroundColour: string;
  borderColour: string;
  textColour: string;
}>`
  display: flex;
  flex: 1;
  align-items: center;
  padding: ${spacing02} ${inputSidePadding};
  background-color: ${props => props.backgroundColour};
  border: solid ${inputBorderWidth} ${props => props.borderColour};
  color: ${props => props.textColour};
`;

const ReadOnlyFieldValue = styled.span`
  font-family: ${fontFamily};
  font-size: ${fontSize03};
`;
